"use strict";
/**
 * v2 artifact-detection types (spec: sdd-artifact-based-scoring-v2).
 *
 * Replaces v1's commit-only classification with an artifact-based SDD
 * detection model: file-vocabulary signal + directory co-location.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.COMMIT_VOCABULARY_BY_FRAMEWORK = exports.RECENT_COMMIT_SAMPLE = exports.FRAMEWORK_REGISTRY = exports.EXCLUSION_DIRS = exports.SDD_ARTIFACT_VOCABULARY = void 0;
exports.registerFrameworkPattern = registerFrameworkPattern;
exports.resetFrameworkRegistry = resetFrameworkRegistry;
exports.resolveFrameworkByRecentCommits = resolveFrameworkByRecentCommits;
/**
 * Canonical SDD artifact filenames. Detection is case-insensitive — callers
 * normalize before comparing. Entries are ordered by signal strength
 * (spec.md > plan.md > tasks.md).
 *
 * Covers the union of speads, spec-driven, Cursor, BMAD, and in-house
 * conventions observed in Vikings scans.
 */
exports.SDD_ARTIFACT_VOCABULARY = [
    'spec.md',
    'plan.md',
    'tasks.md',
    'design.md',
    'prd.md',
    'architecture.md',
    'constitution.md',
    'progress.md',
];
/**
 * Directory names excluded from artifact scanning — vendored / generated /
 * virtualenv dirs that may contain .md files unrelated to SDD practice.
 */
exports.EXCLUSION_DIRS = [
    'node_modules',
    'vendor',
    '.git',
    'target',
    'dist',
    'build',
    'coverage',
    '.next',
    '.venv',
];
/**
 * Seed registry of well-known SDD frameworks. Projects can extend this via
 * `registerFrameworkPattern` (AC-1.3 extensibility — OQ-B config-via-env is
 * resolved in a later task; for now, tests use the in-memory registry API).
 *
 * Matching rule: first entry whose `pathPattern` matches the container path
 * wins. `docs-only` is intentionally broad (`^docs/`) and should be listed
 * last among the seed entries so that more specific frameworks (speads,
 * spec-kit, bmad) take precedence.
 */
exports.FRAMEWORK_REGISTRY = [
    { name: 'speads', pathPattern: /^\.speads\/specs\// },
    { name: 'spec-kit', pathPattern: /^\.specify\/specs\// },
    { name: 'spec-driven-skills', pathPattern: /^spec-driven\// },
    { name: 'bmad', pathPattern: /^\.bmad-core\// },
    { name: 'docs-only', pathPattern: /^docs\// },
];
/**
 * Add or replace a framework pattern at runtime. Returns the registry after
 * mutation — used by tests and (future) config loaders.
 *
 * If an entry with the same `name` already exists, it is replaced in-place;
 * otherwise the new entry is appended. Entries are matched in registry order
 * (first match wins), so register more-specific patterns before broader ones.
 */
function registerFrameworkPattern(entry) {
    const idx = exports.FRAMEWORK_REGISTRY.findIndex((e) => e.name === entry.name);
    if (idx >= 0) {
        exports.FRAMEWORK_REGISTRY[idx] = entry;
    }
    else {
        exports.FRAMEWORK_REGISTRY.push(entry);
    }
    return exports.FRAMEWORK_REGISTRY;
}
/**
 * Reset the framework registry to its seed state. Exposed for tests that
 * mutate the registry and need to restore the default entries between runs.
 */
function resetFrameworkRegistry() {
    exports.FRAMEWORK_REGISTRY.length = 0;
    exports.FRAMEWORK_REGISTRY.push({ name: 'speads', pathPattern: /^\.speads\/specs\// }, { name: 'spec-kit', pathPattern: /^\.specify\/specs\// }, { name: 'spec-driven-skills', pathPattern: /^spec-driven\// }, { name: 'bmad', pathPattern: /^\.bmad-core\// }, { name: 'docs-only', pathPattern: /^docs\// });
}
/**
 * Number of most-recent commits sampled for the framework tiebreak
 * (TASK-1, FR-4 / AC-4.1).
 *
 * Deliberately count-based rather than reusing the scanner's 60-day
 * `COMMIT_WINDOW_DAYS`: a sparse or bursty repo can land a whole framework
 * migration in a handful of commits spread over months, and a date window would
 * sample either nothing or the pre-migration era. A fixed count guarantees a
 * migration-sized sample regardless of commit cadence.
 */
exports.RECENT_COMMIT_SAMPLE = 10;
/**
 * Discriminating commit-message tokens per framework (TASK-1, FR-4 / AC-4.1).
 *
 * Single source of truth for both deterministic detectors (`lambdas/repo-scanner`
 * and `src/detectors/framework.ts`) so the two cannot drift.
 *
 * Two invariants a reviewer cannot recover from the patterns alone:
 *
 *   - Word-boundary anchored, never substring. Substring matching is the `#714`
 *     false-positive class (`specs/defect-framework-classifier-false-positive`),
 *     where e.g. `speadsy` would name speads.
 *   - `TASK-\d+` is deliberately EXCLUDED. It is shared vocabulary across speads
 *     AND pdlc AND spec-kit, so it discriminates nothing; including it would make
 *     the flagship speads->pdlc migration match both candidates and fall back to
 *     the file-tree result, defeating AC-2.1.
 *
 * Non-framework outcomes (`custom-partial`, `none`, `unknown`) and the sentinel
 * activity classifications get no entry — a candidate with no entry can never win
 * the tiebreak. Patterns are non-global: a `/g` regex would carry `lastIndex`
 * across calls and silently skip matches.
 */
exports.COMMIT_VOCABULARY_BY_FRAMEWORK = {
    speads: /\bspeads\b/i,
    pdlc: /\bpdlc\b/i,
    'spec-driven-skills': /\bspec-driven(?:-skills)?\b/i,
    'spec-kit': /\bspec-kit\b/i,
    bmad: /\bbmad\b/i,
    'cai-discovery-skill': /\bcai-discovery(?:-skill)?\b/i,
};
/**
 * Break a framework tie using the vocabulary of a recent-commit sample
 * (TASK-1, FR-4 / AC-4.1; NFR-2).
 *
 * Resolves a candidate ONLY when the commit window is unambiguous: exactly one
 * candidate has vocabulary evidence and every other candidate has none. A window
 * with zero evidence, evidence for two or more candidates, or no input at all is
 * unresolvable and yields `undefined` so the caller's file-tree result stands.
 *
 * The optional return type is how that conservative default is enforced (NFR-2) —
 * callers cannot consume a resolution without handling the no-decision arm, so the
 * fallback is not a code path anyone can forget to write.
 */
function resolveFrameworkByRecentCommits(candidates, messages) {
    const matched = candidates.filter((candidate) => {
        const pattern = exports.COMMIT_VOCABULARY_BY_FRAMEWORK[candidate];
        return pattern !== undefined && messages.some((message) => pattern.test(message));
    });
    return matched.length === 1 ? matched[0] : undefined;
}
//# sourceMappingURL=artifacts.js.map